import { maxFlightChildAge, maxFlightInfantAge, toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { countOccupantsForFlights, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { REWARD_PREVIEW_RATE_BY_CABIN } from 'lib/maldivesCharter/classicRewardPricing'

import type { CalendarItemBundleData } from '../hooks/types'

type PriceMode = 'total' | 'perPerson'

interface BundleDisplayPriceOptions {
  useMemberPrice: boolean
  priceMode: PriceMode
}

interface FallbackBundleDisplayPriceOptions {
  fallbackPricing?: App.PricingWithValue
  fallbackMemberPricing?: App.PricingWithValue
  useMemberPrice: boolean
}

interface FallbackRewardHotelPriceOptions {
  rewardHotelPricing?: {
    price: number | undefined
    memberPrice: number | undefined
  }
  useMemberPrice: boolean
}

function getDisplayValue<T>(regularValue: T | undefined, memberValue: T | undefined, useMemberValue: boolean) {
  return useMemberValue ? memberValue ?? regularValue : regularValue
}

export function getSeatEligiblePassengerCount(occupancies: Array<App.Occupants>) {
  return sumUpOccupancies(
    occupancies,
    new Set<App.OccupantType>(['adults', 'children', 'teenagers']),
    { maxInfantAge: maxFlightInfantAge, maxChildAge: maxFlightChildAge },
  ) || countOccupantsForFlights(occupancies).adults || 1
}

export function getCalendarBundleDisplayPrice(
  bundleData: CalendarItemBundleData,
  { useMemberPrice, priceMode }: BundleDisplayPriceOptions,
) {
  const regularPrice = priceMode === 'perPerson' ? bundleData.pricePerPerson : bundleData.totalPrice
  const memberPrice = priceMode === 'perPerson' ? bundleData.memberPricePerPerson : bundleData.memberTotalPrice

  return getDisplayValue(regularPrice, memberPrice, useMemberPrice)
}

export function getCalendarBundleWasPrice(
  bundleData: CalendarItemBundleData,
  { useMemberPrice, priceMode }: BundleDisplayPriceOptions,
) {
  const regularPrice = priceMode === 'perPerson' ? bundleData.originalPricePerPerson : bundleData.originalTotalPrice
  const memberPrice = priceMode === 'perPerson' ? bundleData.originalMemberPricePerPerson : bundleData.originalMemberTotalPrice

  return getDisplayValue(regularPrice, memberPrice, useMemberPrice)
}

export function getCalendarBundleHotelPrice(
  bundleData: CalendarItemBundleData | undefined,
  useMemberPrice: boolean,
) {
  return getDisplayValue(bundleData?.hotelPrice, bundleData?.hotelMemberPrice, useMemberPrice)
}

export function getFallbackBundleDisplayPrice({
  fallbackPricing,
  fallbackMemberPricing,
  useMemberPrice,
}: FallbackBundleDisplayPriceOptions) {
  return getDisplayValue(fallbackPricing?.price, fallbackMemberPricing?.price, useMemberPrice)
}

export function getFallbackRewardHotelPrice({
  rewardHotelPricing,
  useMemberPrice,
}: FallbackRewardHotelPriceOptions) {
  return getDisplayValue(rewardHotelPricing?.price, rewardHotelPricing?.memberPrice, useMemberPrice)
}

export function getCheapestCalendarBundleData(
  calendarPricingByDate: Record<string, CalendarItemBundleData>,
  { useMemberPrice, priceMode }: BundleDisplayPriceOptions,
) {
  return Object.values(calendarPricingByDate)
    .filter(entry => entry.available && entry.totalPrice > 0)
    .reduce(
      (cheapest, entry) => {
        const entryPrice = getCalendarBundleDisplayPrice(entry, { useMemberPrice, priceMode })
        if (!entryPrice || entryPrice <= 0) return cheapest
        const cheapestPrice = cheapest ? getCalendarBundleDisplayPrice(cheapest, { useMemberPrice, priceMode }) : 0
        return !cheapest || !cheapestPrice || entryPrice < cheapestPrice ? entry : cheapest
      },
      undefined as CalendarItemBundleData | undefined,
    )
}

export function getRewardBundleDisplayPrice({
  fareClass,
  rewardPreviewOnly,
  rewardAvailable,
  rewardSeatPointsPerPerson,
  rewardSeatCashCoPayPerPerson,
  rewardHotelPrice,
  passengerCount,
}: {
  fareClass: FareClassWithReward
  rewardPreviewOnly: boolean
  rewardAvailable: boolean
  rewardSeatPointsPerPerson?: number
  rewardSeatCashCoPayPerPerson?: number
  rewardHotelPrice?: number
  passengerCount: number
}) {
  const rate = REWARD_PREVIEW_RATE_BY_CABIN[toBaseFareClass(fareClass)]
  const usePreview = rewardPreviewOnly || !rewardAvailable || rewardSeatPointsPerPerson === undefined
  const points = usePreview ? rate?.pointsPerPerson : rewardSeatPointsPerPerson
  const cashCoPay = usePreview ? rate?.cashPerPerson : rewardSeatCashCoPayPerPerson
  const hotelPricePerPerson = rewardHotelPrice !== undefined ? rewardHotelPrice / passengerCount : undefined
  const cash = cashCoPay !== undefined && hotelPricePerPerson !== undefined
    ? hotelPricePerPerson + cashCoPay
    : undefined

  return { points, cash }
}
